import React, { useEffect, useRef, useState } from 'react'
import config from '../../config/config'
import { useNavigate } from 'react-router-dom'
import { POI_PATH, EVENT_PATH } from '../../config/routes'
import { slugify, getPoiFullLabel } from '../../utils/utils'
import { useLanguage } from '../../contexts/LanguageContext'

const GoogleMap = ({ pois, selectedPoiTypes, startDate, endDate }) => {
  const mapRef = useRef(null)
  const navigate = useNavigate()
  const { activeLanguage } = useLanguage()
  const [map, setMap] = useState(null)
  const [markers, setMarkers] = useState([])
  const markersRef = useRef([]) // Référence pour stocker les marqueurs

  // Initialisation de la carte et des marqueurs
  useEffect(() => {
    const loadGoogleMapsScript = () => {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      script.async = true
      script.defer = true
      script.onload = loadMap
      document.head.appendChild(script)
    }

    const loadMap = () => {
      if (!window.google) {
        console.warn('Google Maps not loaded!')
        return
      }

      const newMap = new window.google.maps.Map(mapRef.current, {
        center: config.mapCenter,
        zoom: config.mapPoisZoom,
        mapTypeId: 'terrain',
        styles: [
          {
            featureType: 'all',
            elementType: 'labels',
            stylers: [{ visibility: 'on' }],
          },
          {
            featureType: 'landscape.natural',
            elementType: 'geometry',
            stylers: [{ visibility: 'simplified' }, { color: '#7FBF7F' }],
          },
        ],
      })

      setMap(newMap)
    }

    if (!window.google) {
      loadGoogleMapsScript()
    } else {
      loadMap()
    }

    // Nettoyage à la destruction du composant
    return () => {
      markersRef.current.forEach((marker) => marker?.setMap(null))
      markersRef.current = []
    }
  }, []) // Dépendance vide pour ne charger la carte qu'une fois

  // Mise à jour des marqueurs lorsque les POIs changent
  useEffect(() => {
    if (!map) return

    // Supprimer les anciens marqueurs qui ne sont plus dans pois
    markersRef.current.forEach((marker) => {
      if (!pois.find((poi) => poi.id === parseInt(marker.id))) {
        marker.setMap(null)
      }
    })

    // Créer ou mettre à jour les marqueurs pour les nouveaux POIs
    const newMarkers = pois
      .map((poi) => {
        if (isNaN(poi.gpsx) || isNaN(poi.gpsy)) {
          console.warn('Invalid coordinates for', poi.labelFr)
          return null
        }

        // Vérifier si un marqueur existe déjà pour ce POI
        let marker = markersRef.current.find((m) => m.id === poi.id)

        if (!marker) {
          const markerIcon = `/images/markers/marker_50_${poi.poiType.name}.png`
          marker = new window.google.maps.Marker({
            position: { lat: poi.gpsx, lng: poi.gpsy },
            map: null, // Initialement non affiché
            title: getPoiFullLabel(poi, activeLanguage),
            icon: markerIcon,
            id: poi.id,
          })

          marker.addListener('click', () => {
            const linkPath = poi.isEvent ? EVENT_PATH : POI_PATH
            const poiPath = `${linkPath
              .replace(':slug', slugify(poi.labelFr))
              .replace(':id', poi.id)}`
            navigate(poiPath)
          })

          markersRef.current.push(marker)
        }

        return marker
      })
      .filter(Boolean)

    setMarkers(newMarkers)

    // Mettre à jour la visibilité des marqueurs
    updateMarkersVisibility()
  }, [pois, map, activeLanguage, navigate])

  // Mettre à jour la visibilité des marqueurs en fonction des filtres
  const updateMarkersVisibility = () => {
    markersRef.current.forEach((marker) => {
      const poi = pois.find((poi) => poi.id === parseInt(marker.id))
      if (!poi) {
        marker.setMap(null)
        return
      }

      const poiStartDate = new Date(poi.startDate)
      const poiEndDate = poi.endDate ? new Date(poi.endDate) : poiStartDate
      const isDateInRange =
        !poi.isEvent ||
        (startDate === null && endDate === null
          ? true
          : poiStartDate <= endDate && poiEndDate >= startDate)

      const isPoiTypeSelected =
        selectedPoiTypes.length !== 0 &&
        selectedPoiTypes.includes(poi.poiType.name)

      const visible = isDateInRange && isPoiTypeSelected

      marker.setVisible(visible)
      marker.setMap(visible ? map : null)
    })
  }

  // Mettre à jour la visibilité des marqueurs lorsque les filtres changent
  useEffect(() => {
    if (!map) return
    updateMarkersVisibility()
  }, [selectedPoiTypes, startDate, endDate, map])

  return (
    <div
      ref={mapRef}
      style={{ height: '500px', width: '100%', margin: '2rem 0' }}
    ></div>
  )
}

export default GoogleMap
